import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { CalendarClock, ExternalLink, SkipForward, CircleDot } from "lucide-react";
import { toast } from "sonner";

const decisionStyle = {
  auto_yes: { label: "Will record", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  user_override_yes: { label: "Recording (you chose)", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  auto_no_disabled: { label: "Auto-record off", color: "bg-stone-500/15 text-stone-700 dark:text-stone-300 border-stone-500/30" },
  auto_no_short: { label: "Too short", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  auto_no_keyword: { label: "Excluded keyword", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  auto_no_internal: { label: "Internal only", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  auto_no_quota: { label: "Quota reached", color: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30" },
  user_override_no: { label: "Skipped (you chose)", color: "bg-stone-500/15 text-stone-700 dark:text-stone-300 border-stone-500/30" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const now = new Date();
  const diffMs = dt - now;
  const diffMin = Math.round(diffMs / 60000);
  if (Math.abs(diffMin) < 60) return diffMin > 0 ? `in ${diffMin} min` : `${Math.abs(diffMin)} min ago`;
  const sameDay = dt.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = dt.toDateString() === tomorrow.toDateString();
  const timeStr = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today, ${timeStr}`;
  if (isTomorrow) return `Tomorrow, ${timeStr}`;
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) + `, ${timeStr}`;
}

function MeetingCard({ m, onOverride }) {
  const decision = decisionStyle[m.auto_record_decision] || decisionStyle.auto_yes;
  const will = ["auto_yes", "user_override_yes"].includes(m.auto_record_decision);
  const duration = m.scheduled_at && m.ended_at
    ? Math.round((new Date(m.ended_at) - new Date(m.scheduled_at)) / 60000)
    : null;
  const externalCount = (m.attendees || []).filter((a) => {
    if (!a.email) return false;
    const d = a.email.split("@")[1]?.toLowerCase();
    return d && !["gmail.com"].includes(d);
  }).length;

  return (
    <div
      className="rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent/20"
      data-testid={`upcoming-card-${m.meeting_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Badge variant="outline" className={`mb-2 ${decision.color}`} data-testid={`decision-badge-${m.meeting_id}`}>
            <CircleDot className="mr-1 h-2.5 w-2.5" />
            {decision.label}
          </Badge>
          <Link to={`/app/meetings/${m.meeting_id}`} className="block">
            <div className="truncate font-medium hover:underline">{m.title}</div>
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-mono">{relativeTime(m.scheduled_at)}</span>
            {duration && <><span>·</span><span>{duration} min</span></>}
            <span>·</span>
            <span className="capitalize">{m.platform}</span>
            <span>·</span>
            <span>{(m.attendees || []).length} attendees{externalCount > 0 ? " · External" : ""}</span>
          </div>
          {m.auto_record_reason && (
            <div className="mt-2 text-xs text-muted-foreground italic">{m.auto_record_reason}</div>
          )}
        </div>
        <div className="flex -space-x-2 shrink-0">
          {(m.attendees || []).slice(0, 3).map((a, i) => (
            <InitialsAvatar key={i} name={a.name} size="xs" className="ring-2 ring-card" />
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {will ? (
          <Button
            size="sm" variant="outline"
            onClick={() => onOverride(m, "force_skip")}
            data-testid={`skip-meeting-${m.meeting_id}`}
          >
            <SkipForward className="mr-1.5 h-3.5 w-3.5" />
            Skip this meeting
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => onOverride(m, "force_record")}
            data-testid={`record-meeting-${m.meeting_id}`}
          >
            Record this meeting
          </Button>
        )}
        {m.meeting_url && (
          <a
            href={m.meeting_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 text-xs hover:bg-accent/40"
          >
            <ExternalLink className="h-3 w-3" /> Join link
          </a>
        )}
      </div>
    </div>
  );
}

export function UpcomingCalendarWidget() {
  const [items, setItems] = useState(null);
  const [hasCalendar, setHasCalendar] = useState(null);

  const load = async () => {
    try {
      const [eventsRes, statusRes] = await Promise.all([
        api.get("/calendar/events?days=7"),
        api.get("/calendar/status"),
      ]);
      setItems(eventsRes.data.meetings || []);
      setHasCalendar(Boolean(statusRes.data.connection));
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleOverride = async (m, decision) => {
    try {
      await api.post(`/calendar/meetings/${m.meeting_id}/override`, { decision });
      toast.success(decision === "force_skip" ? "Skipped" : "Will record");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    }
  };

  if (items === null) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        Loading upcoming meetings…
      </div>
    );
  }

  if (!hasCalendar && items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="no-calendar-state">
        <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground" />
        <div className="mt-3 text-sm font-medium">No calendar connected</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Sync Google or Outlook to see upcoming meetings here.
        </p>
        <Link to="/app/settings?tab=calendar">
          <Button size="sm" className="mt-4" data-testid="connect-calendar-cta">Connect calendar</Button>
        </Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No upcoming meetings in the next 7 days.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="upcoming-meetings-list">
      {items.map((m) => <MeetingCard key={m.meeting_id} m={m} onOverride={handleOverride} />)}
    </div>
  );
}
